import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from "recharts";
import {
  Bell, Settings, Search, ChevronDown, Users, Briefcase, Clock,
  CheckCircle, Filter, Download, Plus, SkipForward, ChevronRight,
  MoreHorizontal, ArrowUpRight, ArrowDownRight
} from "lucide-react";

const BASE = "#092162";      
const LIGHT = "#EEF2FF";

const hiresPerMonth = [
  { month: "Jan", hires: 10 }, { month: "Feb", hires: 15 },
  { month: "Mar", hires: 12 }, { month: "Apr", hires: 20 },
  { month: "May", hires: 18 }, { month: "Jun", hires: 22 },
  { month: "Jul", hires: 18 }, { month: "Aug", hires: 24 },
  { month: "Sep", hires: 21 }, { month: "Oct", hires: 30 },
  { month: "Nov", hires: 27 }, { month: "Dec", hires: 14 },
];


const timeToHireTrend = [
  { month: "Jul", days: 38 }, { month: "Aug", days: 35 },
  { month: "Sep", days: 33 }, { month: "Oct", days: 30 },
  { month: "Nov", days: 28 }, { month: "Dec", days: 26 },
];
const sourceData = [
  { source: "LinkedIn", candidates: 420 },
  { source: "Referrals", candidates: 310 },
  { source: "Indeed",   candidates: 210 },
  { source: "Glassdoor",candidates: 130 },
  { source: "Direct",   candidates:  90 },
];
const DIVERSITYDATA = [
  { name: "Female",           value: 0, color: "#4F9CF9" },
  { name: "Male",             value: 0, color: "#092162" },
  { name: "Prefer not to answer",value: 0, color: "#94A3B8" },
];

const PIPELINE_TEMPLATE = [
  { stage:"Total Applications", count:0, color:"#CBD5F5" },
  { stage:"AI Interview Stage", count:0, color:"#93C5FD" },
  { stage:"Recruiter Stage", count:0, color:"#4F9CF9" },
  { stage:"Client Interview Stage", count:0, color:"#2563EB" },
  { stage:"Offer Stage", count:0, color:"#1D4ED8" },
  { stage:"Background Check", count:0, color:"#092162" },
];

const alerts = [
  { text:"3 offer letters pending approval",                     urgency:"high",   time:"2h ago" },
  { text:"REQ-1037 expires in 2 days",                          urgency:"high",   time:"4h ago" },
  { text:"5 candidates awaiting interviewer feedback",           urgency:"medium", time:"1d ago" },
  { text:"Interview panel confirmation needed for REQ-1041",     urgency:"medium", time:"1d ago" },
  { text:"Job board posting REQ-1035 expires this week",         urgency:"low",    time:"2d ago" },
];
const engagement = [
  { stage:"Email Open",       rate:68 },
  { stage:"Response Rate",    rate:54 },
  { stage:"Interview Accept", rate:82 },
  { stage:"No-Shows",         rate: 8 },
  { stage:"Offer Accept",     rate:79 },
];

const stageMapping = {
  "Total Applications": [],
  "AI Interview Stage": [
    "AI Interview Scheduled",
    "AI Interview Pass",
    "AI Interview Fail"
  ],

  "Recruiter Stage": [
    "Recruiter Interview Scheduled",
    "Recruiter Interview Pass",
    "Recruiter Interview On-Hold",
    "Recruiter Re-Interview",
    "Technical Interview Scheduled",
    "Technical Interview Pass",
  ],

  "Client Interview Stage": [
    "Submitted to Client",
    "Client Interview Scheduled",
    "Client Interview Pass",
    "Client Interview Fail",
    "Client Interview On-Hold",
    "Client Re-Interview"
  ],

  "Offer Stage": [
    "Offer Letter Sent",
    "Offer Rejected"
  ],

  "Background Check": [
    "Offer Accepted"
  ]
};

const spark = (seed) => Array.from({length:10},(_,i)=>seed+i*1.3+Math.sin(i)*2|0);

const badge = s => ({
  "Open":    "bg-emerald-100 text-emerald-700",
  "Closed":  "bg-slate-100 text-slate-500",
  "On Hold": "bg-amber-100 text-amber-700",
}[s] || "bg-slate-100 text-slate-500");

const dot = u => ({ high:"bg-red-500", medium:"bg-amber-400", low:"bg-emerald-400" }[u]);

function Spark({ data }) {
  const max=Math.max(...data), min=Math.min(...data);
  const pts=data.map((v,i)=>`${(i/9)*60},${24-((v-min)/(max-min||1))*22}`).join(" ");
  return <svg width={60} height={24}><polyline fill="none" stroke={BASE} strokeWidth="2.5" points={pts}/></svg>;
}

function KPI({ icon:Icon, label, value, sub, trend, up, data }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl" style={{backgroundColor:LIGHT}}>
            <Icon size={17} style={{color:BASE}}/>
          </div>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
        </div>
        {trend && (
          <span className={`flex items-center gap-0.5 text-xs font-bold ${up?"text-emerald-600":"text-red-500"}`}>
            {up?<ArrowUpRight size={13}/>:<ArrowDownRight size={13}/>}{trend}
          </span>
        )}
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[28px] font-bold leading-none" style={{color:BASE}}>{value}</div>
          {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
        </div>
        {data && <Spark data={data}/>}
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────
export default function Dashboard({ userName = "User" }) {
  const [tab, setTab]     = useState("overview");
  const [dept, setDept]   = useState("All");
  const [query, setQuery] = useState("");
  const [totalActive, setTotalActive] = useState(0); 
  const [totalApplications, setTotalApplications] = useState(0);
  const [diversityData, setDiversityData] = useState(DIVERSITYDATA);
  const [pipelineStages, setPipelineStages] = useState(PIPELINE_TEMPLATE);
  const [racePercentages, setRacePercentages] = useState([]);
  const [diversityIndex, setDiversityIndex] = useState(0);
  const [jobPostings, setJobPostings] = useState([]);
  const [client, setClient] = useState("All");
  const monthOrder = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const [period, setPeriod] = useState("6M"); 
  const now = new Date();
  const currentMonthIndex = now.getMonth();
  const filteredData = period === "6M" 
    ? hiresPerMonth.slice(-6)
    : hiresPerMonth;
  const clientsList = ["All", ...Array.from(new Set(jobPostings.map(j => j.Clientr).filter(Boolean)))];
  const getColorForRace = (race) => {
  switch (race) {
    case "White": return "#092162";
    case "Asian": return "#4F9CF9";
    case "Black or African American": return "#7C3AED";
    case "American Indian or Alaska Native": return "#10B981";
    case "Two or More Races": return "#F59E0B";
    case "Prefer not to answer": return "#A1A1AA";
    default: return "#D1D5DB";
  }
};
  const [applicationStatusSummary, setApplicationStatusSummary] = useState(0);
  const [genderCounts, setGenderCounts] = useState([]);
  const [error, setError] = useState(null);        
  const [loading, setLoading] = useState(false);  
  const max = Math.max(...pipelineStages.map(s => s.count), 1);

  const navigate = useNavigate();

    const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      setGreeting(getGreeting(now));
    }, 1000); 
    return () => clearInterval(timer);
  }, []);

  function getInitials(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
  const getGreeting = (date) => {
    const hours = date.getHours();
    if (hours < 12) return "Good morning";
    else if (hours < 18) return "Good afternoon";
    else return "Good evening";
  };

  const formattedDate = currentTime.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });


  const fetchActiveRequisitions = async (status = "Active") => {
    try {
      const response = await fetch(
        `/api/getKPIdata/active-requisitions?status=${encodeURIComponent(status)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();


      setTotalActive(data.totalJobs);
      setTotalApplications(data.totalApplications);

    } catch (err) {
      console.error(err);
      setError("Failed to fetch active requisitions");
    } finally {
      setLoading(false);
    }
  };


const fetchApplicationStatusSummary = async () => {
  try {
    const response = await fetch(`/api/getKPIdata/application-status-summary`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();

    const statusCounts = {};

    data.statuses.forEach(item => {
      statusCounts[item.status] = item.count;
    });

    const updatedStages = PIPELINE_TEMPLATE.map(stage => {

      if (stage.stage === "Total Applications") {
        return {
          ...stage,
          count: data.totalApplications || 0
        };
      }

      const mappedStatuses = stageMapping[stage.stage] || [];

      const count = mappedStatuses.reduce(
        (sum, status) => sum + (statusCounts[status] || 0),
        0
      );

      return {
        ...stage,
        count
      };

    });

    setPipelineStages(updatedStages);

  } catch (err) {
    console.error(err);
  }
};

const fetchGenderCounts = async () => {
  try {
    const response = await fetch(`/api/getKPIdata/gender-summary`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();

    const mappedData = diversityData.map(d => {
      const match = data.genders.find(g => g.gender === d.name);
      const count = match ? match.count : 0;

      const percentage = data.totalApplications
        ? (count / data.totalApplications) * 100
        : 0;

      return { 
        ...d, 
        value: Number(percentage.toFixed(1)), 
        count 
      };
    });

    setDiversityData(mappedData);

    const male = mappedData.find(g => g.name === "Male")?.value || 0;
    const female = mappedData.find(g => g.name === "Female")?.value || 0;
    const pm = male / 100;
    const pf = female / 100;
    const diversityIndex = ((1 - (pm * pm + pf * pf)) * 100).toFixed(1);
    setDiversityIndex(diversityIndex);

  } catch (err) {
    console.error("Error fetching gender summary:", err);
    setError("Failed to fetch gender summary");
  } finally {
    setLoading(false);
  }
};

const fetchLastFiveJobs = async () => {
  try {
    const response = await fetch(`/api/getKPIdata/last-five-jobs`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();

    const jobs = Array.isArray(data.lastFiveJobs) ? data.lastFiveJobs : [];

    setJobPostings(jobs);
  } catch (err) {
    console.error("Error fetching last five jobs:", err);
  }
};


  const fetchRaceCounts = async () => {
    try {
      const response = await fetch(`/api/getKPIdata/race-summary`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json(); 
      const total = data.totalApplications;
      const raceData = data.races.slice();

      if (!raceData.find(r => r.race === "Prefer not to answer")) {
        raceData.push({ race: "Prefer not to answer", count: 0 });
      }

      const percentages = raceData.map(r => ({
        name: r.race,
        value: ((r.count / total) * 100).toFixed(1),
        color: getColorForRace(r.race)
      }));

      setRacePercentages(percentages);

    } catch (err) {
      console.error("Error fetching race summary:", err);
      setRacePercentages([]);
    }
  };



useEffect(() => {
  const loadDashboardData = async () => {
    try {
      await Promise.all([
        fetchActiveRequisitions(),
        fetchApplicationStatusSummary(),
        fetchGenderCounts(),
        fetchRaceCounts(),
        fetchLastFiveJobs()
      ]);
    } catch (error) {
      console.error("Dashboard loading error:", error);
    }
  };

  loadDashboardData();
}, []);

 function getLast6MonthsData(currentIndex) {
    let data = [];
    for (let i = 5; i >= 0; i--) {
      const idx = (currentIndex - i + 12) % 12; 
      data.push(hiresPerMonth[idx]);
    }
    return data;
  }
  
const badge = (status) => {
    if (!status) return "bg-slate-100 text-slate-700";
    const s = status.toLowerCase();
    if (s.includes("active")) return "bg-green-100 text-green-700";
    if (s.includes("close") || s.includes("archive")) return "bg-red-100 text-red-700";
    if (s.includes("on-hold")) return "bg-yellow-100 text-yellow-700";
    return "bg-slate-100 text-slate-700";
  };

 const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: "Segoe UI, system-ui, sans-serif" }}
    >
      <main className="p-6 space-y-6 max-w-screen-2xl mx-auto">
        <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <KPI
            icon={Briefcase}
            label="Open Requisitions"
            value={totalActive}
            sub=""
            trend=""
            up={true}
            data={spark(4)}
          />
          <KPI
            icon={Users}
            label="In Pipeline"
            value={totalApplications}
            sub=""
            trend=""
            up={true}
            data={spark(7)}
          />
          <KPI
            icon={Clock}
            label="Avg Time to Hire"
            value="26d"
            sub=""
            trend=""
            up={false}
            data={[13, 12, 11, 11, 10, 9, 9, 8, 8, 7]}
          />
          <KPI
            icon={CheckCircle}
            label="Offer Accept Rate"
            value="79%"
            sub=""
            trend=""
            up={true}
            data={spark(6)}
          />
          <KPI
            icon={Users}
            label="Diversity Index"
            value={`${diversityIndex}%`}
            sub={`Female: ${diversityData.find((g) => g.name === "Female")?.value || 0}% | Male: ${diversityData.find((g) => g.name === "Male")?.value || 0}%`}
            trend=""
            up={true}
            data={spark(5)}
          />
        </section>

        {/* Pipeline funnel */}
        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-[13px]" style={{ color: BASE }}>
              Candidate Pipeline Funnel
            </h2>
          </div>
          <div className="flex items-end gap-3">
            {pipelineStages.map((s, i) => (
              <div
                key={i}
                className="flex-1 flex flex-col items-center gap-1.5"
              >
                <span className="text-[11px] font-bold" style={{ color: BASE }}>
                  {s.count}
                </span>
                <div
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: s.color,
                    height: `${Math.max(18, (s.count / max) * 130)}px`,
                  }}
                />
                <span className="text-[10px] text-slate-500 text-center leading-tight">
                  {s.stage}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Table + Alerts */}
        <div className="grid xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[13px]" style={{ color: BASE }}>
                Recent Job Postings
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                  <Filter size={11} className="text-slate-400" />
                  <select
                    className="text-[12px] bg-transparent outline-none text-slate-600"
                    value={client}
                    onChange={(e) => setClient(e.target.value)}
                  >
                    {clientsList.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <table className="w-full">
              <thead>
                <tr className="text-[11px] text-slate-400 uppercase border-b border-slate-100">
                  {[
                    "Req ID",
                    "Job Title",
                    "Client",
                    "Location",
                    "Status",
                    "Posted",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`pb-2 font-semibold ${
                        i === 4 || i === 5 ? "text-right" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobPostings
                  .filter((j) => client === "All" || j.Clientr === client)
                  .map((j, i) => (
                    <tr
                      key={i}
                      className="border-b border-slate-50 hover:bg-slate-50 transition text-[12px]"
                    >
                      <td className="py-3 font-mono text-slate-400">
                        {j.id || "-"}
                      </td>
                      <td className="py-3 font-semibold text-slate-700">
                        {j.JobTitle || "-"}
                      </td>
                      <td className="py-3 text-slate-500">
                        {j.Clientr || "-"}
                      </td>
                      <td className="py-3 text-slate-500">
                        {j.WorkLocation || "-"}
                      </td>
                      <td className="py-3 text-right">
                        <span
                          className={`text-[11px] font-semibold px-2 py-1 rounded-full ${badge(j.Status)}`}
                        >
                          {j.Status || "-"}
                        </span>
                      </td>
                      <td className="py-3 text-right text-slate-400">
                        {formatDate(j.Created)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[13px]" style={{ color: BASE }}>
                ⚡ Action Items
              </h2>
              <span className="bg-red-100 text-red-600 text-[11px] font-bold px-2 py-0.5 rounded-full">
                {alerts.length} pending
              </span>
            </div>
            <div className="space-y-2.5 flex-1">
              {alerts.map((a, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition cursor-pointer"
                >
                  <div
                    className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dot(a.urgency)}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-slate-700 leading-snug">
                      {a.text}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {a.time}
                    </p>
                  </div>
                  <ChevronRight
                    size={12}
                    className="text-slate-300 mt-1 flex-shrink-0"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {/* Area chart */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[13px]" style={{ color: BASE }}>
                Hires Per Month
              </h2>
              <div className="flex items-center gap-2">
                {["6M", "1Y"].map((l) => (
                  <button
                    key={l}
                    className={`text-[11px] px-2 py-1 rounded-lg ${
                      period === l
                        ? "bg-blue-100 text-blue-600"
                        : "bg-slate-100 text-slate-500"
                    } hover:bg-slate-200`}
                    onClick={() => setPeriod(l)}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <ResponsiveContainer width="100%" height={175}>
              <AreaChart
                data={filteredData}
                margin={{ top: 5, right: 5, left: -22, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={BASE} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={BASE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 10,
                    border: "none",
                    boxShadow: "0 4px 14px rgba(0,0,0,.08)",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="hires"
                  stroke={BASE}
                  strokeWidth={2.5}
                  fill="url(#g1)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Bar: sources */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h2 className="font-bold text-[13px] mb-4" style={{ color: BASE }}>
              Top Sources
            </h2>
            <ResponsiveContainer width="100%" height={175}>
              <BarChart
                data={sourceData}
                layout="vertical"
                margin={{ left: 5, right: 5, top: 0, bottom: 0 }}
              >
                <XAxis
                  type="number"
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="source"
                  tick={{ fontSize: 11 }}
                  width={68}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 10,
                    border: "none",
                  }}
                />
                <Bar dataKey="candidates" fill={BASE} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Progress: engagement */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h2 className="font-bold text-[13px] mb-4" style={{ color: BASE }}>
              Candidate Engagement
            </h2>
            <div className="space-y-3">
              {engagement.map((c, i) => (
                <div key={i}>
                  <div className="flex justify-between text-[12px] mb-1">
                    <span className="text-slate-600 font-medium">
                      {c.stage}
                    </span>
                    <span
                      className="font-bold"
                      style={{
                        color: c.stage === "No-Shows" ? "#EF4444" : BASE,
                      }}
                    >
                      {c.rate}%
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${c.rate}%`,
                        background: c.stage === "No-Shows" ? "#EF4444" : BASE,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Diversity + Time-to-Hire */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Donut */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h2 className="font-bold text-[13px] mb-3" style={{ color: BASE }}>
              Gender Diversity
            </h2>
            <ResponsiveContainer width="100%" height={185}>
              <PieChart>
                <Pie
                  data={diversityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={72}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {diversityData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 10,
                    border: "none",
                  }}
                  formatter={(value, name, props) => {
                    const count = props.payload.count;
                    return `${value}% (${count} applicants)`;
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h2 className="font-bold text-[13px] mb-3" style={{ color: BASE }}>
              Race Breakdown
            </h2>
            <div className="space-y-3 mt-1">
              {racePercentages.map((e, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: e.color }}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between text-[12px] mb-1">
                      <span className="text-slate-600">{e.name}</span>
                      <span className="font-bold" style={{ color: "#000" }}>
                        {e.value}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${e.value}%`,
                          backgroundColor: e.color,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-[13px]" style={{ color: BASE }}>
                Time to Hire Trend
              </h2>
              <span className="text-[11px] text-emerald-600 font-bold flex items-center gap-0.5">
                <ArrowDownRight size={12} /> ↓ 32% since Jul
              </span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart
                data={timeToHireTrend}
                margin={{ top: 5, right: 5, left: -22, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 10,
                    border: "none",
                  }}
                  formatter={(v) => [`${v} days`, "Avg."]}
                />
                <Line
                  type="monotone"
                  dataKey="days"
                  stroke="#EF4444"
                  strokeWidth={2.5}
                  dot={{ fill: "#EF4444", r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </main>
    </div>
  );
}